import React from "react";
import ButtonClose from "shared/ButtonClose/ButtonClose";
import { Disclosure } from "@headlessui/react";
import { NavLink, useLocation } from "react-router-dom";
import { NavItemType } from "./NavigationItem";
import { NAVIGATION_DEMO_2, NAVIGATION_DEMO_3 } from "data/navigation";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import SocialsList from "shared/SocialsList/SocialsList";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import SwitchDarkMode from "shared/SwitchDarkMode/SwitchDarkMode";

import { Link } from "react-router-dom";
import logoLightV2Img from "images/mitzenPixel.png";
import { useCounterStore } from "store/auth";

export interface NavMobileProps {
  data?: NavItemType[];
  data2?: NavItemType[];
  logoImg?: string;
  onClickClose?: () => void;
}

const NavMobile: React.FC<NavMobileProps> = ({
  data = NAVIGATION_DEMO_2,
  data2 = NAVIGATION_DEMO_3,
  logoImg = logoLightV2Img,
  onClickClose,
}) => {
  const location = useLocation();

  const isChildActive = (item: NavItemType) =>
    !!item.children?.some((child) =>
      child.href ? location.pathname.startsWith(child.href) : false
    );

  const _renderMenuChild = (
    item: NavItemType,
    itemClass = " pl-3 text-neutral-900 dark:text-neutral-200 font-medium "
  ) => {
    const rowClasses = `flex text-sm rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 mt-0.5 pr-4 ${itemClass}`;

    return (
      <ul className="nav-mobile-sub-menu pl-6 pb-1 text-base">
        {item.children?.map((i, index) => {
          // Hijo sin descendientes: navega y cierra el menú
          if (!i.children?.length) {
            return (
              <li key={i.href + "" + index}>
                <NavLink
                  to={{
                    pathname: i.href || undefined,
                  }}
                  onClick={onClickClose}
                  className={({ isActive }) =>
                    `${rowClasses} ${isActive ? "text-secondary" : ""}`
                  }
                >
                  <span className="py-2.5 block w-full text-white">
                    {i.name}
                  </span>
                </NavLink>
              </li>
            );
          }

          // Hijo con descendientes: toda la fila abre/cierra el panel
          return (
            <Disclosure
              key={i.href + "" + index}
              as="li"
              defaultOpen={isChildActive(i)}
            >
              {({ open }) => (
                <>
                  <Disclosure.Button
                    type="button"
                    className={`${rowClasses} w-full items-center text-left cursor-pointer select-none`}
                  >
                    <span className="py-2.5 flex-grow text-white">
                      {i.name}
                    </span>
                    <ChevronDownIcon
                      className={`ml-2 h-4 w-4 shrink-0 self-center text-slate-500 transition-transform duration-200 ${open ? "rotate-180" : ""
                        }`}
                      aria-hidden="true"
                    />
                  </Disclosure.Button>

                  <Disclosure.Panel>
                    {_renderMenuChild(
                      i,
                      "pl-3 text-slate-600 dark:text-slate-400 "
                    )}
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          );
        })}
      </ul>
    );
  };

  const _renderItem = (item: NavItemType, index: number) => {
    const rowClasses =
      "flex w-full items-center py-2.5 px-4 font-medium uppercase tracking-wide text-sm hover:bg-[#FFCE00] dark:hover:bg-slate-800 rounded-lg";

    // Item sin hijos: navega y cierra el menú
    if (!item.children?.length) {
      return (
        <li key={index} className="text-white dark:text-white">
          <NavLink
            to={{
              pathname: item.href || undefined,
            }}
            onClick={onClickClose}
            className={({ isActive }) =>
              `${rowClasses} ${isActive ? "text-secondary" : ""}`
            }
          >
            <span className="block w-full">{item.name}</span>
          </NavLink>
        </li>
      );
    }

    // Item con hijos: toda la fila abre/cierra el panel
    return (
      <Disclosure
        key={index}
        as="li"
        className="text-white dark:text-white"
        defaultOpen={isChildActive(item)}
      >
        {({ open }) => (
          <>
            <Disclosure.Button
              type="button"
              className={`${rowClasses} text-left cursor-pointer select-none`}
            >
              <span className="flex-grow">{item.name}</span>
              <ChevronDownIcon
                className={`ml-2 h-4 w-4 shrink-0 text-white transition-transform duration-200 ${open ? "rotate-180" : ""
                  }`}
                aria-hidden="true"
              />
            </Disclosure.Button>

            <Disclosure.Panel>{_renderMenuChild(item)}</Disclosure.Panel>
          </>
        )}
      </Disclosure>
    );
  };

  const renderMagnifyingGlassIcon = () => {
    return (
      <svg
        width={22}
        height={22}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M11.5 21C16.7467 21 21 16.7467 21 11.5C21 6.25329 16.7467 2 11.5 2C6.25329 2 2 6.25329 2 11.5C2 16.7467 6.25329 21 11.5 21Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M22 22L20 20"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  };

  const renderSearchForm = () => {
    return (
      <form
        action=""
        method="POST"
        className="flex-1 text-slate-900 dark:text-slate-200"
      >
        <div className="bg-slate-50 dark:bg-slate-800 flex items-center space-x-1 py-2 px-4 rounded-xl h-full">
          {renderMagnifyingGlassIcon()}
          <input
            type="search"
            placeholder="Type and press enter"
            className="border-none bg-transparent focus:outline-none focus:ring-0 w-full text-sm "
          />
        </div>
        <input type="submit" hidden value="" />
      </form>
    );
  };
  const isLoggedIn = useCounterStore((state) => state.isLoggedIn);
  const token = localStorage.getItem("USER_AUTH");
  return (
    <div className="overflow-y-auto w-full h-screen py-2 transition transform shadow-lg ring-1 dark:ring-neutral-700 dark:bg-neutral-900 divide-y-2 divide-[#FFCE00] dark:divide-neutral-800" style={{ background: "linear-gradient(to top, #001f79, #002fb7, #0000a0)" }}>
      <div className="py-6 px-5 flex justify-center">
        <Link
          to="/"
          className={`ttnc-logo inline-block text-slate-600`}
        >
          {logoImg && (
            <img
              className="max-h-8 sm:max-h-10 dark:block"
              src={logoImg}
              alt="Logo-Light"
            />
          )}
        </Link>
        <span className="absolute right-2 top-2 p-1">
          <ButtonClose className="text-white" onClick={onClickClose} />
        </span>
      </div>
      {isLoggedIn || token ? (
        <ul className="flex flex-col py-6 px-2 space-y-1">
          {data2.map(_renderItem)}
        </ul>
      ) : (
        <ul className="flex flex-col py-6 px-2 space-y-1">
          {data.map(_renderItem)}
        </ul>
      )}
    </div>
  );
};

export default NavMobile;